import { Fragment } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../services/firebase";
import { useAuth } from "../context/AuthContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import {
  LogOut,
  UserCircle,
  ShieldCheck,
  Briefcase,
} from 'lucide-react';
import ToggleDarkMode from "./ToggleDarkMode";
import { useChristmasMode } from "../hooks/useChristmasMode";

export const ActionButton = ({ onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="p-2 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700
               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
               motion-safe:transition-colors motion-reduce:transition-none"
  >
    {children}
  </button>
);

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth(); // Usamos el flag del contexto
  const { isChristmasMode } = useChristmasMode();

  const isUserInAdminView = location.pathname.startsWith('/admin');

  const handleLogout = async () => {
    try {
      // Registrar logout antes de cerrar sesión
      try {
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const functions = getFunctions();
        const logUserLogout = httpsCallable(functions, 'logUserLogout');
        await logUserLogout();
      } catch (logoutError) {
        console.error("Error registrando logout:", logoutError);
        // Continuar con el logout aunque falle el registro
      }
      
      await signOut(auth);
      navigate("/auth");
    } catch (err) {
      console.error("Error al cerrar sesión:", err);
    }
  };

  return (
    <header
      role="banner"
      className={`sticky top-0 z-50 w-full border-b backdrop-blur-md
                  ${location.pathname !== '/' 
                    ? isChristmasMode 
                      ? "bg-white/40 dark:bg-gray-900/70 border-gray-200 dark:border-gray-800" 
                      : "bg-white/80 dark:bg-gray-900/80 border-gray-200 dark:border-gray-800"
                    : "bg-transparent border-transparent"}
                  motion-safe:transition-all motion-safe:duration-300 motion-reduce:transition-none`}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex justify-between items-center
                      ${location.pathname !== '/' ? "h-16" : "h-20"}
                      motion-safe:transition-all motion-safe:duration-300 motion-reduce:transition-none`}
        >
          {/* Logo con enlace condicional */}
          <div className="flex items-center gap-2">
            <Link
              to={isAdmin && isUserInAdminView ? "/admin/dashboard" : "/dashboard"}
              aria-label="Ir al panel principal"
            >
              <img
                src="/AgendateHoy-logo-light.webp"
                alt="AgendateHoy"
                className="h-14 w-auto dark:hidden"
              />
              <img
                src="/AgendateHoy-logo-dark.webp"
                alt="AgendateHoy"
                className="h-14 w-auto hidden dark:block"
              />
            </Link>
          </div>

          {/* Acciones */}
          <nav aria-label="Acciones de usuario y configuración">
            <div className="flex items-center gap-2 sm:gap-4">
              <ToggleDarkMode />

              <Menu as="div" className="relative inline-block text-left">
                <div>
                  <Menu.Button
                    className="p-2 rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700
                               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                               motion-safe:transition-colors motion-reduce:transition-none"
                    aria-label="Abrir menú de usuario"
                  >
                    <UserCircle className="h-6 w-6" />
                  </Menu.Button>
                </div>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-slate-800 shadow-lg
                               ring-1 ring-black/5 focus:outline-none"
                  >
                    <div className="px-1 py-1">
                      {isAdmin && (
                        <>
                          <div className="px-2 py-2">
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Cambiar Vista</p>
                          </div>
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to="/dashboard"
                                className={`${active ? "bg-slate-100 dark:bg-slate-700" : ""}
                                            group flex w-full items-center rounded-md px-2 py-2 text-sm text-slate-900 dark:text-slate-100`}
                              >
                                <Briefcase className="mr-2 h-5 w-5" />
                                Vista Profesional
                              </Link>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to="/admin/dashboard"
                                className={`${active ? "bg-slate-100 dark:bg-slate-700" : ""}
                                            group flex w-full items-center rounded-md px-2 py-2 text-sm text-slate-900 dark:text-slate-100`}
                              >
                                <ShieldCheck className="mr-2 h-5 w-5" />
                                Vista Administrador
                              </Link>
                            )}
                          </Menu.Item>
                          <div className="my-1 h-px bg-slate-200 dark:bg-slate-700" />
                        </>
                      )}

                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={handleLogout}
                            className={`${active ? "bg-blue-500 text-white" : "text-gray-900 dark:text-gray-200"}
                                        group flex w-full items-center rounded-md px-2 py-2 text-sm
                                        motion-safe:transition-colors motion-reduce:transition-none`}
                          >
                            <LogOut className="mr-2 h-5 w-5" aria-hidden="true" />
                            Cerrar sesión
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}